import { useNavigate } from "react-router-dom";
import { imgSrc, primaryImage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrustBadge } from "@/components/TrustBadge";
import { Users, Plus, Camera, ShieldQuestion, EyeOff, ClipboardCheck, Pencil } from "lucide-react";

const OnboardingNudge = ({ models }) => {
  const navigate = useNavigate();
  const incomplete = models.filter((m) => !m.onboarding?.complete);
  if (incomplete.length === 0) return null;
  return (
    <div data-testid="dashboard-onboarding-nudge" className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border-2 border-[hsl(43,74%,70%)] bg-[hsl(43,74%,95%)] p-3">
      <div className="flex items-start gap-2.5 text-sm">
        <EyeOff className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(43,60%,30%)]" />
        <span className="text-[hsl(43,60%,22%)]">
          <span className="font-semibold">
            {incomplete.length === 1
              ? "1 profile is hidden from the marketplace."
              : `${incomplete.length} profiles are hidden from the marketplace.`}
          </span>{" "}
          Complete the ID scan, required photos and AI metrics to go live.
        </span>
      </div>
      <Button data-testid="dashboard-onboarding-button" size="sm" className="rounded-xl"
        onClick={() => navigate(`/models/${incomplete[0].id}/onboarding`)}>
        <ClipboardCheck className="mr-1 h-3.5 w-3.5" /> Continue onboarding
      </Button>
    </div>
  );
};

const VerifyNudge = ({ user, models }) => {
  const navigate = useNavigate();
  const show = user.role === "model"
    && models.length > 0
    && models.every((m) => m.onboarding?.complete)
    && models.some((m) => (m.verification?.status || "unverified") !== "verified");
  if (!show) return null;
  return (
    <div data-testid="dashboard-verify-nudge" className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border-2 border-[hsl(43,74%,70%)] bg-[hsl(43,74%,95%)] p-3">
      <div className="flex items-start gap-2.5 text-sm">
        <ShieldQuestion className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(43,60%,30%)]" />
        <span className="text-[hsl(43,60%,22%)]">
          <span className="font-semibold">Get verified.</span> Upload a selfie so clients see the
          Verified badge — verified looks get prioritised in trust filters.
        </span>
      </div>
      <Button data-testid="dashboard-verify-button" size="sm" className="rounded-xl"
        onClick={() => navigate(`/models/${models[0].id}/edit`)}>
        Verify identity
      </Button>
    </div>
  );
};

const RosterItem = ({ model }) => {
  const navigate = useNavigate();
  const img = imgSrc(primaryImage(model));
  return (
    <div data-testid="dashboard-roster-card" className="flex gap-3 rounded-xl border bg-card p-3">
      <div className="h-20 w-16 shrink-0 overflow-hidden rounded-lg bg-muted">
        {img && <img src={img} alt={model.name} className="h-full w-full object-cover" />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate font-semibold">{model.name}</p>
          <TrustBadge trust={model.trust} />
          {!model.onboarding?.complete && (
            <span data-testid="roster-hidden-badge" className="inline-flex items-center gap-1 rounded-full border border-[hsl(43,74%,70%)] bg-[hsl(43,74%,92%)] px-2 py-0.5 text-[10px] font-semibold text-[hsl(43,60%,22%)]">
              <EyeOff className="h-2.5 w-2.5" /> Hidden
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground">{(model.images || []).length} photo{(model.images || []).length === 1 ? "" : "s"} · {model.available ? "available" : "unavailable"}</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {!model.onboarding?.complete && (
            <Button data-testid="roster-onboarding-button" size="sm" className="h-7 rounded-lg text-xs"
              onClick={() => navigate(`/models/${model.id}/onboarding`)}>
              <ClipboardCheck className="mr-1 h-3 w-3" /> Onboarding
            </Button>
          )}
          <Button data-testid="dashboard-edit-model-button" size="sm" variant="outline" className="h-7 rounded-lg border bg-card text-xs hover:bg-muted"
            onClick={() => navigate(`/models/${model.id}/edit`)}>
            <Pencil className="mr-1 h-3 w-3" /> Edit
          </Button>
          {model.onboarding?.complete && (
            <Button size="sm" variant="ghost" className="h-7 rounded-lg text-xs" onClick={() => navigate(`/models/${model.id}`)}>
              View public
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export const RosterCard = ({ user, stats, isAgency }) => {
  const navigate = useNavigate();
  const models = stats?.models || [];
  return (
    <Card className="rounded-2xl">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Users className="h-4 w-4" /> {isAgency ? `Roster (${stats?.models_count || 0})` : "My model profile"}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <OnboardingNudge models={models} />
        <VerifyNudge user={user} models={models} />
        {models.length === 0 ? (
          <div className="rounded-xl border border-dashed p-8 text-center">
            <Camera className="mx-auto h-7 w-7 text-muted-foreground" />
            <p className="mt-2 font-display text-lg">{isAgency ? "No models on your roster yet" : "You haven't created your profile yet"}</p>
            <p className="mt-1 text-sm text-muted-foreground">Add photos, features and skills so clients can find {isAgency ? "your models" : "you"}.</p>
            <Button data-testid="dashboard-create-model-button" className="mt-4 rounded-xl" onClick={() => navigate("/models/new")}>
              <Plus className="mr-1.5 h-4 w-4" /> {isAgency ? "Add first model" : "Create my profile"}
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {models.map((m) => <RosterItem key={m.id} model={m} />)}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
